import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import type { SubmitterGroup } from '../compose';
import { CategorySubsection } from './CategorySubsection';

interface SubmitterCardProps {
  group: SubmitterGroup;
  onToggleInclude?: (entryId: string, next: boolean) => void;
  onSaveDraft?: (entryId: string, md: string) => Promise<void> | void;
  onSavePeriod?: (entryId: string, mbrPeriod: string) => Promise<void> | void;
  onRegenerate?: (entryId: string) => Promise<void> | void;
}

function initialsFromAlias(alias: string): string {
  const first = alias.replace(/[^a-zA-Z]/g, '')[0];
  return first ? first.toUpperCase() : '?';
}

/**
 * One card per submitter for the current month, surfacing their categorized
 * entries. Header is Avatar + alias; body is a stack of CategorySubsection's
 * separated by horizontal rules.
 */
export function SubmitterCard({
  group,
  onToggleInclude = () => {},
  onSaveDraft = () => {},
  onSavePeriod = () => {},
  onRegenerate = () => {},
}: SubmitterCardProps) {
  return (
    <Card className="overflow-hidden py-0">
      <CardHeader className="flex flex-row items-center gap-3 border-b border-slate-100 px-6 py-4">
        <Avatar className="h-9 w-9">
          <AvatarFallback>{initialsFromAlias(group.submitterAlias)}</AvatarFallback>
        </Avatar>
        <h2 className="text-base font-semibold text-slate-900">
          {group.submitterAlias}
        </h2>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-4 py-4">
        {group.byCategory.map((c, idx) => (
          <div key={c.category}>
            {idx > 0 && <Separator className="mb-4" />}
            <CategorySubsection
              category={c.category}
              entries={c.entries}
              onToggleInclude={onToggleInclude}
              onSaveDraft={onSaveDraft}
              onSavePeriod={onSavePeriod}
              onRegenerate={onRegenerate}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
